import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import Input from '@ecp/common/src/components/input/bo/Input';
import Tree from '@ecp/common/src/components/tree/bo/Tree';

const Container = styled(FlexBoxColumn)`
  width: 100%;
`;

const TreeWrapper = styled.div`
  width: 100%;
  height: 542px;
`;

const SubsidiarySearchTree = ({ subsidiaryList, onSelect, type, searchKeyword, defaultKeyword }) => {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(false);
  const [inputValue, setInputValue] = useState(defaultKeyword || searchKeyword);

  const isSite = useMemo(() => type !== 'subsidiary', [type]);
  const tree = useMemo(() => {
    const newTree = [];
    subsidiaryList.forEach((subsidiary) => {
      const mySite = newTree.find(({ key }) => key === subsidiary.siteNumber);
      if (mySite) {
        mySite.children.push({
          title: subsidiary.subsidiaryCompanyName,
          key: `${subsidiary.siteNumber}-${subsidiary.subsidiaryCompanyCode}`,
        });
      } else {
        newTree.push({
          title: subsidiary.siteName,
          key: subsidiary.siteNumber,
          children: isSite
            ? []
            : [
                {
                  title: subsidiary.subsidiaryCompanyName,
                  key: `${subsidiary.siteNumber}-${subsidiary.subsidiaryCompanyCode}`,
                },
              ],
        });
      }
    });

    return newTree;
  }, [subsidiaryList, isSite]);

  useEffect(() => {
    if (inputValue?.length > 0) {
      const newExpandedKeys = subsidiaryList
        .filter(
          ({ siteName, subsidiaryCompanyName, siteNumber = 'ALL' }) =>
            siteNumber &&
            (siteName?.toUpperCase().includes(inputValue.toUpperCase()) ||
              subsidiaryCompanyName?.toUpperCase().includes(inputValue.toUpperCase()))
        )
        .map(({ siteNumber = 'ALL' }) => siteNumber);
      setExpandedKeys([...newExpandedKeys]);
      setAutoExpandParent(true);
    }
  }, [inputValue, setExpandedKeys, setAutoExpandParent, subsidiaryList]);

  const handleTreeExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
      setAutoExpandParent(false);
    },
    [setExpandedKeys, setAutoExpandParent]
  );

  const handleSelect = useCallback(
    ([key]) => {
      if ((!isSite && key?.split('-')?.length === 2) || isSite) {
        setSelectedKeys([key]);
        onSelect?.(key);
      }
    },
    [onSelect, setSelectedKeys, isSite]
  );

  const handleValueChange = useCallback((e) => {
    setInputValue(e?.target?.value);
  }, []);

  const handleEnter = useCallback((e) => {
    // 검색 form에서 enter 처리되는 것 방지
    e.stopPropagation();
  }, []);

  return (
    <Container>
      <Input width={'100%'} onEnter={handleEnter} onChange={handleValueChange} value={inputValue} />
      <Spacing bottom={10} />
      <TreeWrapper>
        <Tree
          rootless
          autoExpandParent={autoExpandParent}
          data={tree}
          onExpand={handleTreeExpand}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          searchKeyword={inputValue}
          onSelect={handleSelect}
        />
      </TreeWrapper>
    </Container>
  );
};

SubsidiarySearchTree.propTypes = {
  type: PropTypes.oneOf(['site', 'subsidiary']),
  subsidiaryList: PropTypes.array,
  onSelect: PropTypes.func,
  searchKeyword: PropTypes.string,
  defaultKeyword: PropTypes.string,
};

SubsidiarySearchTree.defaultProps = {
  type: 'subsidiary',
  searchKeyword: '',
};

export default SubsidiarySearchTree;
